import React, { useEffect } from "react";
import { useSetRecoilState } from "recoil";
import { userIdAtom, tokenAtom, userRoleAtom } from "../../store";
import { useAuth0 } from "@auth0/auth0-react";

interface IUserProviderProps {
  children: React.ReactNode;
}

const ROLE_NAMESPACE = "https://myapa-api//role";
const authEnabled = process.env.REACT_APP_AUTH_ENABLED !== "false";
const ADMIN_ROLE_PRIORITY = [
  "system_admin",
  "account_admin",
  "training_librarian",
  "user",
];

const LocalUserProvider = ({ children }: IUserProviderProps) => {
  const setToken = useSetRecoilState(tokenAtom);
  const setUserId = useSetRecoilState(userIdAtom);
  const setUserRole = useSetRecoilState(userRoleAtom);

  useEffect(() => {
    setToken("local-dev-token");
    setUserId("local-dev-user");
    setUserRole("system_admin");
  }, [setToken, setUserId, setUserRole]);

  return <>{children}</>;
};

const Auth0UserProvider = ({ children }: IUserProviderProps) => {
  const setToken = useSetRecoilState(tokenAtom);
  const setUserId = useSetRecoilState(userIdAtom);
  const setUserRole = useSetRecoilState(userRoleAtom);

  const { user, isLoading, loginWithRedirect, getAccessTokenSilently } =
    useAuth0();

  useEffect(() => {
    const checkUserLoggedIn = async () => {
      if (!isLoading && !user) {
        await loginWithRedirect();
      } else if (!isLoading && user) {
        try {
          const accessToken = await getAccessTokenSilently();
          setToken(accessToken ?? null);
          if (user?.sub) {
            setUserId(user.sub);
          }

          // Decode JWT to get role
          const tokenPayload = JSON.parse(atob(accessToken.split(".")[1]));
          const userRoles = tokenPayload[ROLE_NAMESPACE] || ["user"];
          const primaryRole =
            ADMIN_ROLE_PRIORITY.find((role) => userRoles.includes(role)) ??
            userRoles[0];
          setUserRole(primaryRole);
        } catch (error) {
          console.error("Error fetching credentials:", error);
          setToken(null);
        }
      }
    };

    checkUserLoggedIn();
  }, [
    user,
    isLoading,
    loginWithRedirect,
    getAccessTokenSilently,
    setToken,
    setUserId,
    setUserRole,
  ]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!user) return null;

  return <>{children}</>;
};

export const UserProvider = (props: IUserProviderProps) => {
  if (!authEnabled) {
    return <LocalUserProvider {...props} />;
  }

  return <Auth0UserProvider {...props} />;
};
